import React, { useMemo, useState } from "react";
import { Alert, Button, Checkbox, Modal, Space, Spin, Tag, Typography, message } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { authAxios } from "../../utils/session_utils";

/**
 * "Sync Promos" — materialize PROMOS registry rows as playout-ready segments.
 *
 * Click → GET /snowflake/promos/sync-segments/preview (dry-run diff) →
 * confirmation dialog with the per-reason breakdown and per-item tick boxes →
 * confirm → POST /snowflake/promos/sync-segments with the selection. The BE
 * re-runs the same diff at submit time, so a stale dialog can never queue
 * something the fresh diff wouldn't. One batch-media-processor job per promo;
 * segments appear here once the jobs report back (minutes, not instant).
 *
 * The dialog IS the operator's inclusion gate: once a promo segment exists,
 * the Marathon export attaches it automatically.
 */

interface DiffEntry {
	promoId: string;
	amagiId: string;
	promoType: string | null;
	seriesTitle: string | null;
	refId: string | null;
	skipReason?: string;
	skipDetail?: string;
	headUncertain?: boolean;
}

interface OrphanSegment {
	segmentId: string;
	segmentIdentifier: string;
	title: string | null;
	promoType: string | null;
}

interface PreviewResults {
	candidates: Record<"new" | "changed" | "missing" | "failed", DiffEntry[]>;
	deletions: OrphanSegment[];
	inFlight: DiffEntry[];
	skipped: DiffEntry[];
	upToDate: number;
	totals: { toProcess: number; toDelete: number; inFlight: number; skipped: number };
}

const REASON_LABELS: Record<string, string> = {
	new: "New — never processed",
	changed: "Changed — registry edited since last processing",
	missing: "Missing — processed before, segment gone or unplayable",
	failed: "Failed — last processing attempt errored, will retry",
};

const entryLabel = (e: DiffEntry) =>
	[e.seriesTitle, e.amagiId, e.promoType && e.promoType !== "NextOn" ? `(${e.promoType})` : null]
		.filter(Boolean)
		.join(" ");

const SyncPromosButton: React.FC = () => {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [preview, setPreview] = useState<PreviewResults | null>(null);
	const [selectedPromoIds, setSelectedPromoIds] = useState<Set<string>>(new Set());
	const [selectedDeletionIds, setSelectedDeletionIds] = useState<Set<string>>(new Set());

	const allCandidates = useMemo(
		() =>
			preview
				? ([] as DiffEntry[]).concat(
						preview.candidates.new,
						preview.candidates.changed,
						preview.candidates.missing,
						preview.candidates.failed
				  )
				: [],
		[preview]
	);

	const openPreview = async () => {
		setLoading(true);
		setOpen(true);
		setPreview(null);
		try {
			const response = await authAxios({
				method: "GET",
				url: `${process.env.REACT_APP_BACK_END_API}/snowflake/promos/sync-segments/preview`,
			});
			const results: PreviewResults = response.data.results;
			setPreview(results);
			// Everything actionable starts ticked; unticking is the operator's
			// per-item veto (e.g. leaving English NextOns unmaterialized).
			setSelectedPromoIds(
				new Set(
					([] as DiffEntry[])
						.concat(results.candidates.new, results.candidates.changed, results.candidates.missing, results.candidates.failed)
						.map((c) => String(c.promoId))
				)
			);
			setSelectedDeletionIds(new Set(results.deletions.map((d) => String(d.segmentId))));
		} catch (err) {
			console.error("promo sync preview failed", err);
			setOpen(false);
			message.error("Failed to load the promo sync preview");
		} finally {
			setLoading(false);
		}
	};

	const togglePromo = (promoId: string, checked: boolean) => {
		setSelectedPromoIds((prev) => {
			const next = new Set(prev);
			if (checked) next.add(promoId);
			else next.delete(promoId);
			return next;
		});
	};

	const toggleDeletion = (segmentId: string, checked: boolean) => {
		setSelectedDeletionIds((prev) => {
			const next = new Set(prev);
			if (checked) next.add(segmentId);
			else next.delete(segmentId);
			return next;
		});
	};

	const setSection = (entries: DiffEntry[], checked: boolean) => {
		setSelectedPromoIds((prev) => {
			const next = new Set(prev);
			for (const e of entries) {
				if (checked) next.add(String(e.promoId));
				else next.delete(String(e.promoId));
			}
			return next;
		});
	};

	const handleConfirm = async () => {
		setSubmitting(true);
		try {
			const response = await authAxios({
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/snowflake/promos/sync-segments`,
				headers: { "Content-Type": "application/json" },
				data: JSON.stringify({
					promoIds: Array.from(selectedPromoIds),
					deleteSegmentIds: Array.from(selectedDeletionIds),
				}),
			});
			const results = response.data.results;
			setOpen(false);
			Modal.success({
				title: "Promo sync queued",
				content: (
					<div>
						<p>{results.queued} processing job(s) queued; segments will appear here as jobs complete.</p>
						{results.deleted > 0 && <p>{results.deleted} orphaned segment(s) removed.</p>}
						{results.failed?.length > 0 && (
							<p>
								{results.failed.length} could not be queued:{" "}
								{results.failed.map((f: any) => `${f.amagiId} (${f.error})`).join(", ")}
							</p>
						)}
						{results.notQueued?.length > 0 && (
							<p>{results.notQueued.length} no longer qualified when re-checked and were skipped.</p>
						)}
					</div>
				),
			});
		} catch (err) {
			console.error("promo sync failed", err);
			message.error("Failed to queue the promo sync");
		} finally {
			setSubmitting(false);
		}
	};

	const renderSection = (reason: keyof PreviewResults["candidates"]) => {
		const entries = preview?.candidates[reason] ?? [];
		if (!entries.length) return null;
		const selectedCount = entries.filter((e) => selectedPromoIds.has(String(e.promoId))).length;
		return (
			<div key={reason} style={{ marginBottom: 12 }}>
				<Space style={{ marginBottom: 4 }}>
					<Typography.Text strong>
						{REASON_LABELS[reason]} ({selectedCount}/{entries.length})
					</Typography.Text>
					<Button size="small" type="link" onClick={() => setSection(entries, true)}>
						all
					</Button>
					<Button size="small" type="link" onClick={() => setSection(entries, false)}>
						none
					</Button>
				</Space>
				<div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 4, padding: 8 }}>
					{entries.map((e) => (
						<div key={e.promoId}>
							<Checkbox
								checked={selectedPromoIds.has(String(e.promoId))}
								onChange={(ev) => togglePromo(String(e.promoId), ev.target.checked)}
							>
								{entryLabel(e)}
								{e.headUncertain && <Tag style={{ marginLeft: 6 }}>source unverified</Tag>}
							</Checkbox>
						</div>
					))}
				</div>
			</div>
		);
	};

	const queueCount = allCandidates.filter((c) => selectedPromoIds.has(String(c.promoId))).length;
	const deleteCount = preview?.deletions.filter((d) => selectedDeletionIds.has(String(d.segmentId))).length ?? 0;
	const nothingToDo =
		preview !== null && preview.totals.toProcess === 0 && preview.deletions.length === 0;

	return (
		<>
			<Button icon={<SyncOutlined />} onClick={openPreview}>
				Sync Promos
			</Button>
			<Modal
				title="Sync Promos → Segments"
				open={open}
				onCancel={() => setOpen(false)}
				width={640}
				footer={[
					<Button key="cancel" onClick={() => setOpen(false)}>
						Cancel
					</Button>,
					<Button
						key="confirm"
						type="primary"
						loading={submitting}
						disabled={loading || (queueCount === 0 && deleteCount === 0)}
						onClick={handleConfirm}
					>
						{`Queue ${queueCount} job${queueCount === 1 ? "" : "s"}`}
						{deleteCount > 0 ? ` + remove ${deleteCount}` : ""}
					</Button>,
				]}
			>
				{loading && (
					<div style={{ textAlign: "center", padding: 24 }}>
						<Spin tip="Comparing the promo registry against segments…" />
					</div>
				)}
				{!loading && preview && (
					<Space direction="vertical" style={{ width: "100%" }} size="small">
						{nothingToDo && (
							<Alert
								type="success"
								showIcon
								message={`Everything is up to date (${preview.upToDate} promo segment${preview.upToDate === 1 ? "" : "s"} current).`}
							/>
						)}
						{preview.totals.inFlight > 0 && (
							<Alert
								type="info"
								showIcon
								message={`${preview.totals.inFlight} promo(s) already processing — excluded from this run.`}
							/>
						)}
						{(["new", "changed", "missing", "failed"] as const).map(renderSection)}
						{preview.deletions.length > 0 && (
							<div>
								<Typography.Text strong>
									Removals — segment exists but its registry promo was deleted
								</Typography.Text>
								<div style={{ maxHeight: 120, overflowY: "auto", border: "1px solid #f0f0f0", borderRadius: 4, padding: 8 }}>
									{preview.deletions.map((d) => (
										<div key={d.segmentId}>
											<Checkbox
												checked={selectedDeletionIds.has(String(d.segmentId))}
												onChange={(ev) => toggleDeletion(String(d.segmentId), ev.target.checked)}
											>
												{d.title || d.segmentIdentifier}
												{d.promoType && <Tag style={{ marginLeft: 6 }}>{d.promoType}</Tag>}
											</Checkbox>
										</div>
									))}
								</div>
							</div>
						)}
						{preview.skipped.length > 0 && (
							<Alert
								type="warning"
								showIcon
								message={`${preview.skipped.length} promo(s) skipped — fix or delete these registry rows`}
								description={
									<ul style={{ margin: 0, paddingLeft: 18 }}>
										{preview.skipped.map((sk) => (
											<li key={sk.promoId}>
												<strong>{sk.amagiId || `promo ${sk.promoId.slice(0, 8)}…`}</strong>:{" "}
												{sk.skipDetail || sk.skipReason}
											</li>
										))}
									</ul>
								}
							/>
						)}
						{!nothingToDo && (
							<Typography.Text type="secondary">
								Queued promos become playable segments once processing completes. A materialized
								NextOn attaches automatically after its episode on the next Marathon export —
								untick anything you don&apos;t want on air.
							</Typography.Text>
						)}
					</Space>
				)}
			</Modal>
		</>
	);
};

export default SyncPromosButton;
